import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { SUPPORTED_LANGUAGES } from '../i18n/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { cn } from '../utils/classNames';
import { LANGUAGE_NAMES } from '../constants/languages';

interface LanguageSwitcherProps {
  className?: string;
  compact?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  className = '',
  compact = false
}) => {
  const { locale, setLocale } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);



  const handleLanguageChange = (lang: string) => {
    setLocale(lang as typeof SUPPORTED_LANGUAGES[number]);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (typeof document === 'undefined') return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <AnimatePresence mode="wait">
      {!compact ? (
        <motion.div 
          ref={dropdownRef}
          className={`relative ${className}`}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ 
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1] // Apple's cubic-bezier easing
          }}
        >
          <motion.button
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200",
              "text-neutral-700 hover:bg-neutral-100/80"
            )}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              background: 'rgba(255, 255, 255, 0.7)',
              boxShadow: isOpen ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
            }}
          >
            <span className="font-medium">{isOpen ? LANGUAGE_NAMES[locale] : locale.toUpperCase()}</span>
            <motion.div
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </motion.div>
          </motion.button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: -5, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -5, scale: 0.95 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="absolute right-0 mt-1 w-40 rounded-xl overflow-hidden z-50"
                style={{ 
                  transformOrigin: 'top right',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)',
                }}
              >
                <div className="py-1">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <motion.button
                      key={lang}
                      onClick={() => handleLanguageChange(lang)}
                      className={cn(
                        "flex w-full items-center px-4 py-2.5 text-sm text-left transition-colors",
                        "text-neutral-700 hover:bg-neutral-100/60"
                      )}
                      whileHover={{ x: 2 }}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="font-medium">{LANGUAGE_NAMES[lang]}</span>
                        {locale === lang && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="h-2 w-2 rounded-full bg-neutral-900"
                          />
                        )}
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}; 